import React from 'react';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '@/components/utils/translations';
import { localiseChatLabel } from '@/lib/chatBlockLabels';

const NAMED_SLICE_COLORS = ['#0A1A2F', '#00C2C2', '#1C485C', '#5C6B7A', '#89CDD4', '#56B0AF'];
const OTHER_SLICE_FILL = '#CBD5E1';

function sliceFill(slice, index) {
  if (slice?.other === true) return OTHER_SLICE_FILL;
  return NAMED_SLICE_COLORS[index % NAMED_SLICE_COLORS.length];
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  return n.toLocaleString();
}

function ChartTooltip({ active, payload, unit }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-md bg-[#0A1A2F] px-2.5 py-1.5 text-xs text-white shadow-md">
      <p className="font-medium">{row.label}</p>
      <p className="text-white/90 tabular-nums">
        {formatCount(row.value)} {unit}
      </p>
    </div>
  );
}

function ChartNote({ note }) {
  if (!note) return null;
  return <p className="mt-1.5 text-[11px] text-[#5C6B7A] leading-snug">{note}</p>;
}

function PieBlock({ slices, unit }) {
  return (
    <div className="h-52 flex items-center gap-3">
      <div className="w-[46%] h-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius={36}
              outerRadius={68}
              paddingAngle={1}
            >
              {slices.map((slice, index) => (
                <Cell key={`${slice.label}-${index}`} fill={sliceFill(slice, index)} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip unit={unit} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="flex-1 space-y-1.5 min-w-0 pr-1">
        {slices.map((slice, index) => (
          <li key={`${slice.label}-${index}`} className="flex items-center justify-between text-sm gap-2">
            <span className="flex items-center gap-2 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ background: sliceFill(slice, index) }}
              />
              <span className="truncate text-[#0A1A2F]">{slice.label}</span>
            </span>
            <span className="font-medium text-[#0A1A2F] tabular-nums shrink-0">
              {formatCount(slice.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BarBlock({ slices, unit }) {
  return (
    <div className="h-52">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={slices} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#5C6B7A' }}
            stroke="#5C6B7A"
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#5C6B7A' }}
            stroke="#5C6B7A"
            allowDecimals={false}
            width={40}
          />
          <Tooltip content={<ChartTooltip unit={unit} />} cursor={{ fill: 'rgba(0, 194, 194, 0.08)' }} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} name={unit}>
            {slices.map((slice, index) => (
              <Cell key={`${slice.label}-${index}`} fill={sliceFill(slice, index)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ChatBlockChart({ block }) {
  const { t, language } = useTranslation();
  const L = (s) => localiseChatLabel(s, t, language);
  // Display copies only: slice order, values and the `other` flag are unchanged.
  const slices = (Array.isArray(block?.slices) ? block.slices : []).map((s) => ({ ...s, label: L(s.label) }));
  if (!slices.length) return null;
  const unit = L(block.unit || 'institutions');

  return (
    <div>
      {block.title ? (
        <p className="text-sm font-semibold text-[#0A1A2F] mb-2">{L(block.title)}</p>
      ) : null}
      {block.chart === 'bar' ? (
        <BarBlock slices={slices} unit={unit} />
      ) : (
        <PieBlock slices={slices} unit={unit} />
      )}
      <ChartNote note={L(block.note)} />
    </div>
  );
}
